import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import {ChromeIcon} from '../components/ui/ChromeIcon.tsx'

import {
  Plane,
  Hamburger,
  Shirt,
  Dumbbell,
  Quote,
  Monitor,
  GraduationCap,
  Briefcase,
  Smile,
  PawPrint,
  Camera,
  Palette,
  Music,
  Music2,
  Sparkles,
  HeartHandshake,
  TreePine,
  Film,
  BookOpen,
  DollarSign,
  Building2,
  Sparkles as Spirituality,
  Hammer,
  Baby,
  Calendar,
  Brain,
  Home,
  Trophy,
  Megaphone,
  Speech,
  Blocks,
  Globe,
  Instagram,
  Twitter,
  Linkedin,
  Folder,
  Tag
} from "lucide-react";
import { TikTokIcon } from "@/components/TikTokIcon";
import { RedditIcon } from "@/components/RedditIcon";

export type CategoryType =
  | "travel"
  | "food_and_drink"
  | "fashion"
  | "fitness"
  | "motivation"
  | "quotes"
  | "tech"
  | "education"
  | "career"
  | "memes"
  | "pets"
  | "photography"
  | "art_and_design"
  | "music"
  | "dance"
  | "beauty_and_skincare"
  | "relationships"
  | "nature"
  | "movies_and_shows"
  | "books"
  | "finance"
  | "business"
  | "spirituality"
  | "diy_and_crafts"
  | "parenting"
  | "events_and_celebrations"
  | "mental_health"
  | "home_and_decor"
  | "sports"
  | "activism_and_social_issues"
  | "others";

export type PlatformType = "all_platforms" | "instagram" | "twitter" | "linkedin" | "tiktok" | "reddit" | "chrome_bookmark";

export interface Platform {
  id: PlatformType;
  display_name: string;
  api_value: string | null;
  icon: string;
}

export function getCategoryIcon(category: string, is_folder: boolean, has_label: boolean) {
  if (is_folder) {
    return Folder;
  }
  const categoryMap: Record<CategoryType, React.ComponentType<any>> = {
    travel: Plane,
    food_and_drink: Hamburger,
    fashion: Shirt,
    fitness: Dumbbell,
    motivation: Speech,
    quotes: Quote,
    tech: Monitor,
    education: GraduationCap,
    career: Briefcase,
    memes: Smile,
    pets: PawPrint,
    photography: Camera,
    art_and_design: Palette,
    music: Music,
    dance: Music2,
    beauty_and_skincare: Sparkles,
    relationships: HeartHandshake,
    nature: TreePine,
    movies_and_shows: Film,
    books: BookOpen,
    finance: DollarSign,
    business: Building2,
    spirituality: Spirituality,
    diy_and_crafts: Hammer,
    parenting: Baby,
    events_and_celebrations: Calendar,
    mental_health: Brain,
    home_and_decor: Home,
    sports: Trophy,
    activism_and_social_issues: Megaphone,
    others: Blocks
  };

  if (category === "others" && has_label){
    return Tag
  }

  return categoryMap[category as CategoryType] || Globe;
}

// Function to process text and identify usernames
export function processUsernames(text: string, platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT'): Array<{ type: 'text' | 'username', content: string, username?: string, url?: string }> {
  if (!text) return [];

  // Split text by spaces to identify potential usernames
  const parts = text.split(/(\s+)/);
  const processedParts: Array<{ type: 'text' | 'username', content: string, username?: string, url?: string }> = [];

  parts.forEach((part) => {
    // Check if part is a username (starts with @ and contains only alphanumeric characters, underscores, and dots)
    // TikTok also supports @ mentions like Instagram and X
    if (part.startsWith('@') && /^@[a-zA-Z0-9_.]+$/.test(part) && platform !== 'LINKEDIN') {
      const username = part.substring(1); // Remove the @ symbol
      let baseUrl: string;
      if (platform === 'INSTAGRAM') {
        baseUrl = 'https://www.instagram.com';
      } else if (platform === 'X') {
        baseUrl = 'https://x.com';
      } else if (platform === 'TIKTOK') {
        baseUrl = 'https://www.tiktok.com';
      } else if (platform === 'REDDIT') {
        baseUrl = 'https://www.reddit.com/user';
      } else {
        baseUrl = 'https://www.instagram.com'; // fallback
      }

      const profileUrl = `${baseUrl}/${username}`;

      processedParts.push({
        type: 'username',
        content: part,
        username: username,
        url: profileUrl
      });
    } else {
      // Regular text part
      processedParts.push({
        type: 'text',
        content: part
      });
    }
  });

  return processedParts;
}

export function getPlatformIcon(platformId: string) {
  const platformIconMap: Record<string, React.ComponentType<any>> = {
    all_platforms: Globe,
    instagram: Instagram,
    twitter: Twitter,
    linkedin: Linkedin,
    tiktok: TikTokIcon,
    reddit: RedditIcon,
    chrome_bookmark: ChromeIcon
  };

  return platformIconMap[platformId] || Globe; // Default to Globe icon if platform not found
}

export function getPlatforms(): Platform[] {
  return [
    {
      id: "all_platforms",
      display_name: "All Platforms",
      api_value: null,
      icon: "globe"
    },
    {
      id: "instagram",
      display_name: "Instagram",
      api_value: "INSTAGRAM",
      icon: "instagram"
    },
    {
      id: "twitter",
      display_name: "X",
      api_value: "X",
      icon: "twitter"
    },
    {
      id: "linkedin",
      display_name: "LinkedIn",
      api_value: "LINKEDIN",
      icon: "linkedin"
    },
    {
      id: "tiktok",
      display_name: "TikTok",
      api_value: "TIKTOK",
      icon: "tiktok"
    },
    {
      id: "reddit",
      display_name: "Reddit",
      api_value: "REDDIT",
      icon: "reddit"
    },
    {
      id: "chrome_bookmark",
      display_name: "Bookmarks",
      api_value: "CHROME_BOOKMARK",
      icon: "chrome_bookmark"
    }
  ];
}

export function getPlatformApiValue(platformId: string): string | null {
  const platforms = getPlatforms();
  const platform = platforms.find(p => p.id === platformId);
  return platform ? platform.api_value : null;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function buildCategoryFilterParams(selectedCategory: { id: string, category_enum: string, category_display_value: string, is_folder: boolean } | null): { category?: string, label?: string } {
  if (!selectedCategory || selectedCategory.id === '0' || selectedCategory.id === '') {
    return {};
  }

  // If category_enum is "others" - send category id and if category_display_value is not "Others" then send it as label else send label as None
  if (selectedCategory.category_enum === 'others') {
    let params: { category: string; label?: string } = { category: selectedCategory.id };
    if (selectedCategory.category_display_value !== 'Others') {
      params.label = selectedCategory.category_display_value;
    }
    return params;
  }

  // If category_enum is not "others" - send category and label as None
  return {
    category: selectedCategory.id
  };
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - The currency code (e.g., USD, EUR)
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
}

/**
 * Extracts hashtags from text content and returns both hashtags and cleaned text
 * @param text - The text content to extract hashtags from
 * @returns Object containing hashtags array and cleaned text
 */
export const extractHashtags = (text: string): { hashtags: string[]; cleanedText: string } => {
  if (!text) return { hashtags: [], cleanedText: '' };

  // Regex to match hashtags: # followed by letters, numbers, and underscores
  const hashtagRegex = /#([a-zA-Z0-9_]+)/g;
  const matches = text.match(hashtagRegex);

  if (!matches) return { hashtags: [], cleanedText: text };

  // Remove the # symbol and get unique hashtags
  const hashtags = [...new Set(matches.map(tag => tag.slice(1)))];

  // Remove hashtags from the original text
  const cleanedText = text.replace(hashtagRegex, '').replace(/\n\s*\n/g, '\n\n').trim();

  return { hashtags, cleanedText };
};
